/**
 * useDisclosure Hook
 * 
 * Manages open/close state patterns used across components.
 * Provides consistent state management for modals, dropdowns, and other disclosure components.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseDisclosureOptions {
  /**
   * Initial open state
   */
  defaultOpen?: boolean;
  /**
   * Whether the disclosure is controlled externally
   */
  controlled?: boolean;
  /**
   * External open state (when controlled)
   */
  isOpen?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Callback when opening
   */
  onOpen?: () => void;
  /**
   * Callback when closing
   */
  onClose?: () => void;
  /**
   * Whether to prevent closing when clicking outside
   */
  preventCloseOnOutsideClick?: boolean;
  /**
   * Whether to prevent closing when pressing Escape
   */
  preventCloseOnEscape?: boolean;
  /**
   * Whether to restore focus when closing
   */
  restoreFocus?: boolean;
}

export interface UseDisclosureReturn {
  /**
   * Whether the disclosure is open
   */
  isOpen: boolean;
  /**
   * Open the disclosure
   */
  open: () => void;
  /**
   * Close the disclosure
   */
  close: () => void;
  /**
   * Toggle the disclosure state
   */
  toggle: () => void;
  /**
   * Set the disclosure state
   */
  setOpen: (isOpen: boolean) => void;
  /**
   * Whether the disclosure is controlled externally
   */
  isControlled: boolean;
}

export function useDisclosure(
  options: UseDisclosureOptions = {}
): UseDisclosureReturn {
  const {
    defaultOpen = false,
    controlled = false,
    isOpen: externalIsOpen,
    onOpenChange,
    onOpen,
    onClose,
    preventCloseOnOutsideClick = false,
    preventCloseOnEscape = false,
    restoreFocus = true,
  } = options;

  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isControlled = controlled || externalIsOpen !== undefined;

  const isOpen = isControlled ? (externalIsOpen ?? false) : internalIsOpen;

  const open = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(true);
    } else {
      setInternalIsOpen(true);
    }
    onOpen?.();
  }, [isControlled, onOpenChange, onOpen]);

  const close = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setInternalIsOpen(false);
    }
    onClose?.();
  }, [isControlled, onOpenChange, onClose]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const setOpen = useCallback((newIsOpen: boolean) => {
    if (newIsOpen) {
      open();
    } else {
      close();
    }
  }, [open, close]);

  // Handle focus management
  useEffect(() => {
    if (isOpen && restoreFocus) {
      // Store current focus when opening
      previousFocusRef.current = document.activeElement as HTMLElement;
    } else if (!isOpen && restoreFocus && previousFocusRef.current) {
      // Restore focus when closing
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen, restoreFocus]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen || preventCloseOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, preventCloseOnEscape, close]);

  return {
    isOpen,
    open,
    close,
    toggle,
    setOpen,
    isControlled,
  };
}

/**
 * Hook for managing multiple disclosure states
 */
export function useDisclosureGroup(
  disclosures: Record<string, UseDisclosureOptions> = {}
): Record<string, UseDisclosureReturn> {
  const result: Record<string, UseDisclosureReturn> = {};

  for (const [key, options] of Object.entries(disclosures)) {
    result[key] = useDisclosure(options);
  }

  return result;
}

/**
 * Hook for managing mutually exclusive disclosures (only one can be open at a time)
 */
export function useMutuallyExclusiveDisclosure(
  keys: string[],
  options: UseDisclosureOptions = {}
): Record<string, UseDisclosureReturn> {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const result: Record<string, UseDisclosureReturn> = {};

  for (const key of keys) {
    result[key] = useDisclosure({
      ...options,
      isOpen: activeKey === key,
      onOpenChange: (isOpen) => {
        if (isOpen) {
          setActiveKey(key);
        } else if (activeKey === key) {
          setActiveKey(null);
        }
        options.onOpenChange?.(isOpen);
      },
    });
  }

  return result;
}
